import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useMemo } from 'react';
import get from 'lodash/get';
import uniqueId from 'lodash/uniqueId';
import omit from 'lodash/omit';
import classnames from 'classnames';
import { useUncontrolled } from 'uncontrollable';
import { ControlLabel, FormControl, FormGroup, InputGroup, Row } from '@cancercentrum/rcc-bootstrap';
import RegvarInput from '../RegvarInput';
import { VanillaReviewerCheckbox } from '../ReviewerCheckbox/Vanilla';
import { HintText } from '../HintText';
import { Explanation } from '../Explanation';
import { htmlParser } from '../../utils/formatters';
import ImportMenu from './Import/ImportMenu';
const VanillaQuestion = (props)=>{
    const { regvarMetadata, regvarRegisterDocumentation, label, labelHelp, labelHelpTitle, labelTooltip, className, showReviewerCheckbox, help, inline, error, warning, labelClassName, wrapperClassName, isValueImported, onImportValueChange, importableValues, appendAfter, prepend, append, componentClass, hideLabel, ...rest } = props;
    const id = useMemo(()=>props.id || uniqueId('Question_'), [
        props.id
    ]);
    const { value, onValueChange, include, onIncludeChange } = useUncontrolled(props, {
        value: 'onValueChange',
        include: 'onIncludeChange'
    });
    const propsToPass = omit(rest, [
        'ctxDataPath',
        'ctxCondition',
        'ctxValidationGroup',
        'include',
        'value',
        'onValueChange',
        'onIncludeChange'
    ]);
    const labelText = Object.prototype.hasOwnProperty.call(props, 'label') ? label : regvarMetadata?.label || regvarRegisterDocumentation?.label || '[etikett saknas]';
    const labelTooltipText = Object.prototype.hasOwnProperty.call(props, 'labelTooltip') ? labelTooltip : get(regvarMetadata, 'description');
    const labelHelpText = Object.prototype.hasOwnProperty.call(props, 'labelHelp') ? labelHelp : htmlParser(regvarRegisterDocumentation?.helpText);
    const labelNode = labelTooltipText ? /*#__PURE__*/ _jsx(HintText, {
        text: labelText,
        tooltip: labelTooltipText
    }) : labelText;
    const isCheckbox = regvarMetadata.dataType === 'boolean';
    const helpNode = help && /*#__PURE__*/ _jsx("div", {
        className: "form-text",
        children: help
    });
    const errorNodes = [];
    let labelHelpNode = null;
    if (labelHelpText) {
        labelHelpNode = /*#__PURE__*/ _jsx(Explanation, {
            title: labelHelpTitle,
            children: labelHelpText
        });
    }
    error && errorNodes.push(/*#__PURE__*/ _jsx(FormControl.Feedback, {
        type: "error",
        children: error
    }, "error"));
    warning && errorNodes.push(/*#__PURE__*/ _jsx(FormControl.Feedback, {
        type: "warning",
        children: warning
    }, "warning"));
    const formGroupClassName = classnames(className, {
        'show-reviewer-checkbox': showReviewerCheckbox,
        'question__is-imported': isValueImported
    });
    let reviewerCheckboxNode = null;
    if (showReviewerCheckbox && !rest.readOnly) {
        if (isCheckbox) {
            reviewerCheckboxNode = /*#__PURE__*/ _jsx(VanillaReviewerCheckbox, {
                value: include,
                onChange: onIncludeChange
            });
        } else {
            reviewerCheckboxNode = /*#__PURE__*/ _jsx(InputGroup.Addon, {
                children: /*#__PURE__*/ _jsx(VanillaReviewerCheckbox, {
                    value: include,
                    onChange: onIncludeChange
                })
            });
        }
    }
    let appendAfterContent = null;
    if (appendAfter) {
        appendAfterContent = /*#__PURE__*/ _jsx("div", {
            className: "append-content",
            children: appendAfter
        });
    }
    const InputComponent = componentClass || RegvarInput;
    const importMenu = !propsToPass.disabled && onImportValueChange && importableValues ? /*#__PURE__*/ _jsx(ImportMenu, {
        importableValues: importableValues,
        label: labelText,
        isValueImported: !!isValueImported,
        currentValue: value,
        onImportValueChange: onImportValueChange
    }) : null;
    if (isCheckbox && inline && !hideLabel) {
        propsToPass.label = /*#__PURE__*/ _jsxs("span", {
            className: "me-2",
            children: [
                labelNode,
                " ",
                labelHelpNode
            ]
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        className: classnames('question', `regvar-${regvarMetadata.dataType}`, {
            inheritReviewerCheckboxWidth: !!InputComponent.inheritReviewerCheckboxWidth
        }),
        children: /*#__PURE__*/ _jsxs(FormGroup, {
            componentClass: Row,
            controlId: id,
            className: formGroupClassName,
            validationState: error ? 'error' : warning ? 'warning' : undefined,
            children: [
                !!labelText && !hideLabel && !inline && /*#__PURE__*/ _jsxs(ControlLabel, {
                    column: true,
                    className: classnames('question__label', {
                        checkbox: isCheckbox
                    }, labelClassName),
                    children: [
                        labelNode,
                        " ",
                        labelHelpNode
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: classnames('question__wrapper', wrapperClassName),
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: classnames('question__wrapper__input__container', {
                                'appended-content-container': !!appendAfter
                            }),
                            children: [
                                /*#__PURE__*/ _jsxs(InputGroup, {
                                    className: "question-input-wrapper",
                                    children: [
                                        prepend,
                                        /*#__PURE__*/ _jsx(InputComponent, {
                                            ...propsToPass,
                                            regvarMetadata: regvarMetadata,
                                            id: id,
                                            value: value,
                                            onChange: onValueChange
                                        }),
                                        reviewerCheckboxNode,
                                        append,
                                        importMenu
                                    ]
                                }),
                                appendAfterContent
                            ]
                        }),
                        helpNode,
                        errorNodes
                    ]
                })
            ]
        })
    });
};
export default /*#__PURE__*/ memo(VanillaQuestion);
